import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Badge, Button, Form, Table } from 'react-bootstrap';
import { changesApi } from '../../services/changesApi';
import type { ChangeDetailPageTabSlot, ChangeDetailTabDefinition } from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import ChangeDetailTabBuilder from './components/ChangeDetailTabBuilder';

interface SortableRowProps {
  slot: ChangeDetailPageTabSlot;
  tab?: ChangeDetailTabDefinition;
  onToggleVisible: (tab: ChangeDetailTabDefinition) => void;
  onEdit: (tabId: number) => void;
}

const SortableLayoutRow: React.FC<SortableRowProps> = ({ slot, tab, onToggleVisible, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.key,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isCustom = slot.kind === 'custom';

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="align-middle" style={{ width: 48 }}>
        <button
          type="button"
          className="btn btn-light btn-sm border-0 px-2"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          aria-label={`Reordenar ${slot.label}`}
          title="Segure e arraste para mudar a ordem na ficha"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
      </td>
      <td className="align-middle">
        {slot.label}
        {slot.kind === 'fixed' ? (
          <Badge bg="secondary" className="ms-2">
            Sistema
          </Badge>
        ) : null}
      </td>
      <td className="align-middle">
        {isCustom && tab ? (
          <Form.Check
            type="switch"
            id={`detail-tab-hub-visible-${tab.id}`}
            checked={tab.is_visible}
            onChange={() => onToggleVisible(tab)}
            aria-label={`Visível ${tab.label}`}
          />
        ) : (
          <span className="text-muted small">—</span>
        )}
      </td>
      <td className="align-middle small text-muted">
        {isCustom && tab ? (tab.visible_statuses || []).join(', ') || '—' : '—'}
      </td>
      <td className="align-middle text-end">
        {isCustom && tab ? (
          <Button size="sm" variant="outline-primary" onClick={() => onEdit(tab.id)}>
            Editar
          </Button>
        ) : null}
      </td>
    </tr>
  );
};

const ChangeDetailTabsHubPage: React.FC = () => {
  const [slots, setSlots] = useState<ChangeDetailPageTabSlot[]>([]);
  const [tabsById, setTabsById] = useState<Record<number, ChangeDetailTabDefinition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [layout, tabsPayload] = await Promise.all([
        changesApi.getDetailPageTabLayout(),
        changesApi.listDetailTabs(),
      ]);
      setSlots(layout.slots);
      const map: Record<number, ChangeDetailTabDefinition> = {};
      tabsPayload.items.forEach((tab) => {
        map[tab.id] = tab;
      });
      setTabsById(map);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar abas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleVisible = async (tab: ChangeDetailTabDefinition) => {
    try {
      await changesApi.updateDetailTab(tab.id, { is_visible: !tab.is_visible });
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar visibilidade.'));
    }
  };

  const createTab = async () => {
    if (!newLabel.trim()) return;
    try {
      const created = await changesApi.createDetailTab({
        label: newLabel.trim(),
        visible_statuses: ['autorizar'],
      });
      setNewLabel('');
      setEditingId(created.id);
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar a aba.'));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = slots.findIndex((item) => item.key === active.id);
    const newIndex = slots.findIndex((item) => item.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(slots, oldIndex, newIndex);
    setSlots(next);
    try {
      const saved = await changesApi.replaceDetailPageTabLayout(next.map((item) => item.key));
      setSlots(saved.slots);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível gravar a ordem das abas.'));
      await load();
    }
  };

  const activeSlot = useMemo(
    () => slots.find((item) => item.key === activeKey) || null,
    [activeKey, slots],
  );

  if (editingId != null) {
    return (
      <ChangeDetailTabBuilder
        tabId={editingId}
        onBack={() => setEditingId(null)}
        onSaved={() => void load()}
      />
    );
  }

  return (
    <div>
      <p className="text-muted mb-1">
        Configure abas extras na ficha da mudança: campos, status visíveis, Novo, paginação e busca.
      </p>
      <p className="text-muted small mb-3">
        Arraste qualquer linha (Sistema ou customizada) para definir a ordem na ficha da mudança. Abas
        customizadas novas entram antes de «Resumo IA», salvo se você reposicionar.
      </p>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form.Control
          size="sm"
          style={{ maxWidth: 280 }}
          placeholder="Nome da nova aba"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
        />
        <Button size="sm" onClick={() => void createTab()} disabled={!newLabel.trim()}>
          Criar aba
        </Button>
      </div>
      {loading ? (
        <p className="text-muted">Carregando…</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => setActiveKey(String(event.active.id))}
          onDragCancel={() => setActiveKey(null)}
          onDragEnd={(event) => {
            setActiveKey(null);
            void handleDragEnd(event);
          }}
        >
          <SortableContext items={slots.map((item) => item.key)} strategy={verticalListSortingStrategy}>
            <Table responsive hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th aria-label="Ordem" style={{ width: 48 }} />
                  <th>Aba</th>
                  <th>Visível</th>
                  <th>Status</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => (
                  <SortableLayoutRow
                    key={slot.key}
                    slot={slot}
                    tab={slot.tab_id ? tabsById[slot.tab_id] : undefined}
                    onToggleVisible={(row) => void toggleVisible(row)}
                    onEdit={setEditingId}
                  />
                ))}
              </tbody>
            </Table>
          </SortableContext>
          <DragOverlay>
            {activeSlot ? (
              <div className="border rounded bg-white shadow-sm px-3 py-2">
                <strong>{activeSlot.label}</strong>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default ChangeDetailTabsHubPage;
